/* eslint-disable @typescript-eslint/naming-convention */
import type { FunctionComponent, ReactElement } from 'react';
import { cloneElement } from 'react';
import { useDispatch } from 'react-redux';
import type { SetRequired } from 'type-fest';
import type { IAsync, Project, RouterProps } from '../../../types';
import { capitalizeWord, getProjectBaseURLFromParams } from '../../../utils';
import Loader from '../../generic/Loader';
import Redirect from '../../generic/Redirect';
import NavigationStore from '../../../redux/navigation';
import NotFound from '../../generic/NotFound';
import getLatestRevisionIdFromProject from '../shared/helpers/getLatestRevisionIdFromProject';

type ProjectRouteParams = {
  project_id: string;
  reference_type: 'branch' | 'tag';
  reference: string;
};

type ProjectOverviewPageProps = RouterProps<ProjectRouteParams> & {
  // references is guarenteed to be returned from `/api/projects/:locator`.
  // TODO: We *should* change the actual typing of Project being passed around
  project?: IAsync<SetRequired<Project, 'references'>>;
  onProjectChange: () => void;
  children?: ReactElement;
};

const ProjectOverviewPage: FunctionComponent<ProjectOverviewPageProps> = ({
  project,
  params,
  children,
  onProjectChange,
}) => {
  const dispatch = useDispatch();

  if (!project?.data || project.status.loading) {
    return (
      <div className="container margin-top">
        <Loader />
      </div>
    );
  }

  // Branch provided in url must match a project branch to continue forward
  const branchInUrlExists = project.data.references.some(
    r => r.type === params.reference_type && r.name === params.reference,
  ) || false;

  if (!branchInUrlExists) {
    // Show the branch dropdown in the nav as a visual cue
    dispatch(
      NavigationStore.actions.setNavState({
        expand: 'project-expanded',
        navProps: {
          showBranchDropdown: true,
          shouldUseCallToAction: true,
        },
      }),
    );
    return <NotFound heading={`${capitalizeWord(params.reference_type) || 'Branch'} "${params.reference}" not found`} />;
  }

  if (children) {
    return cloneElement(
      children,
      {
        project,
        onProjectChange,
      },
    );
  }

  // If a user hits this page directly, AND the branch they provided exists, try to redirect
  if (!children) {
    const revisionId = getLatestRevisionIdFromProject(project);
    if (revisionId) {
      return (
        <Redirect
          replace
          to={getProjectBaseURLFromParams({
            ...params,
            revision_id: revisionId,
          })}
        />
      );
    }
  }

  return <NotFound />;
};

export default ProjectOverviewPage;
